import React, { useCallback, useEffect, useState } from 'react';
import Typography from '../../../components/atoms/typography';
import CustomDataGrid from '../../../components/atoms/dataGrid';
import useNearGetCarts, { GetCarts, Row } from '../../../hooks/carts/useNearGetCarts';
import ActivityIndicator from '../../../components/ActivityIndicator';
import { Box } from '@material-ui/core';
import { GridSelectionModel } from '@mui/x-data-grid';
import { useFormContext } from 'react-hook-form';
import { formatDateTime } from '../../../utils/formatTime';
import { FreightDetails } from '../../../components/molecules/import/configureFreight';
import { defaultFormatKilometrageNumbers } from '../../../utils/formatKilometrageNumber';

const columns = [
  {
    field: 'corporateName',
    flex: 1.2,
    editable: false,
    renderHeader: () => (
      <Typography color="gray" fontWeight="700">
        PDV
      </Typography>
    ),
  },
  {
    field: 'distance',
    flex: 1,
    editable: false,
    renderHeader: () => (
      <Typography color="gray" fontWeight="700">
        Distância (Km)
      </Typography>
    ),
  },
  {
    field: 'totalWeight',
    flex: 1,
    editable: false,
    renderHeader: () => (
      <Typography color="gray" fontWeight="700">
        Peso Total (Kg)
      </Typography>
    ),
  },
  {
    field: 'createdAt',
    flex: 1,
    editable: false,
    valueFormatter: (params: any) => formatDateTime(params.value),
    renderHeader: () => (
      <Typography color="gray" fontWeight="700">
        Criado em
      </Typography>
    ),
  },
  {
    field: 'updatedAt',
    flex: 1,
    editable: false,
    valueFormatter: (params: any) => formatDateTime(params.value),
    renderHeader: () => (
      <Typography color="gray" fontWeight="700">
        Reprocessado em
      </Typography>
    ),
  },
];

const FreightConfigurationTable: React.FC = () => {
  const { getValues, setValue } = useFormContext<FreightDetails>();
  const freightDetails = getValues();
  const [selectedCartIds, setSelectedCartIds] = useState<string[]>(freightDetails.extraCartIds || []);
  const [currentPage, setCurrentPage] = useState(1);
  const [nearCartsResult, setNearCartsResult] = useState<GetCarts>({
    rows: [],
    totalRecords: 0,
    page: 1,
  });

  const { execute, loading } = useNearGetCarts();

  const setExtraCarts = (carts: Row[]) => setValue('extraCarts', carts, { shouldValidate: true, shouldDirty: true })

  const onWeightChange = (newWeight: number) => {
    setValue('totalWeight', freightDetails.originalTotalWeight + newWeight, { shouldValidate: true, shouldDirty: true });
  };

  const handleSelectionChange = (selectionModel: GridSelectionModel) => {
    const newSelectedCartIds = selectionModel.map(id => id.toString());

    if (arraysAreEqual(newSelectedCartIds, selectedCartIds)) {
      return;
    }

    let weightChange = 0;

    nearCartsResult.rows.forEach(cart => {
      if (newSelectedCartIds.includes(cart.cartId)) {
        weightChange += Number(cart.totalWeight);
        setValue('totalDeliveries', Number(freightDetails.totalDeliveries) + 1, { shouldValidate: true, shouldDirty: true });
      }
    });

    setSelectedCartIds(newSelectedCartIds);
    setExtraCarts(nearCartsResult.rows.filter(cart => newSelectedCartIds.includes(cart.cartId)));
    onWeightChange(weightChange);
  };

  function arraysAreEqual(cartList: any, deliveryList: any) {
    return cartList.length === deliveryList.length && cartList.every((value: any, index: any) => value === deliveryList[index]);
  }

  const loadCarts = useCallback(async () => {
    const result = await execute({
      page: currentPage,
      rowsPerPage: 5,
      freightRequestId: freightDetails.freightRequestId,
    });

    const filteredRows = result.rows.filter((cart: any) =>
      !selectedCartIds.includes(cart.cartId)
    );

    setNearCartsResult({
      ...result,
      rows: filteredRows,
    });
  }, [currentPage, freightDetails.freightRequestId, selectedCartIds]);


  useEffect(() => {
    if (freightDetails.extraCarts) {
      setSelectedCartIds(freightDetails.extraCarts.map((cart: any) => cart.cartId));
    }
    loadCarts();
  }, [freightDetails.freightRequestId]);

  if (loading) {
    return <ActivityIndicator />;
  }
  return (
    <>
      {nearCartsResult.rows.length === 0 ?
        (<Box component="div">
          <Typography fontWeight="700" color="secondary">
            Reprocessamento
          </Typography>
          <Typography mt={4} fontWeight="400" color="secondary">
            Não existem entregas reprocessadas disponíveis.
          </Typography>
        </Box>) : (<Box component="div">
          <Typography fontWeight="700" color="secondary">
            Reprocessamento
          </Typography>
          <Typography mt={4} mb={4} fontWeight="400" color="secondary">
            Existem entregas disponíveis para serem reprocessadas, gostaria de incluí-las neste frete?
          </Typography>
          <CustomDataGrid
            columns={columns}
            rows={nearCartsResult.rows.map(el => ({
              ...el,
              totalWeight: Number(el.totalWeight).toLocaleString('pt-BR', {
                unit: 'kilogram',
                style: 'unit',
                maximumFractionDigits: 2,
              }),
              distance: defaultFormatKilometrageNumbers(el.distance),
            }))}
            checkboxSelection
            getRowId={row => row.cartId}
            selectionModel={selectedCartIds}
            onSelectionModelChange={handleSelectionChange}
            disableColumnFilter
            disableColumnMenu
            disableColumnSelector
            pageSize={5}
          />
        </Box>)
      }
    </>
  );
};

export default FreightConfigurationTable;

